import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Validate JWT token without using hooks
 */
const validateToken = () => {
  const storedToken = localStorage.getItem('plateos_token');
  
  if (!storedToken) {
    return false;
  }

  try {
    // JWT tokens are base64 encoded with 3 parts separated by dots
    const parts = storedToken.split('.');
    if (parts.length !== 3) {
      localStorage.removeItem('plateos_token');
      localStorage.removeItem('plateos_user');
      return false;
    }

    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    const now = new Date().getTime();

    if (exp && now >= exp * 1000) {
      // Token is expired
      localStorage.removeItem('plateos_token');
      localStorage.removeItem('plateos_user');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    localStorage.removeItem('plateos_token');
    localStorage.removeItem('plateos_user');
    return false;
  }
};

/**
 * Get appropriate login redirect based on current domain and current path
 */
const getLoginRedirect = () => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  
  // FIRST: Check if we're on a tenant subdomain (e.g., tenant.plateos.io)
  // Tenant subdomains should NEVER redirect to /superadmin/login
  if (hostname.endsWith('.plateos.io') && hostname !== 'www.plateos.io') {
    // If user was trying to access /admin path, redirect back to /admin (shows login)
    if (pathname.startsWith('/admin')) {
      return '/admin';
    }
    return '/login';  // Tenant login page
  }
  
  // Preview/development environments - check the current path to determine redirect
  if (hostname.includes('preview.emergentagent.com') || 
      hostname.includes('preview.emergent') ||
      hostname.includes('localhost')) {
    // If user was trying to access /admin path, redirect back to /admin
    if (pathname.startsWith('/admin')) {
      return '/admin';
    }
    return '/login';  // Use tenant login for preview by default
  }
  
  // Main domain (plateos.io) - redirect to superadmin login
  if (hostname === 'plateos.io' || hostname === 'www.plateos.io') {
    return '/superadmin/login';
  }
  
  // Default fallback
  return '/login';
};

/**
 * ProtectedRoute component that ensures users are authenticated
 * before accessing protected parts of the application.
 * 
 * - Redirects to appropriate login based on domain (main vs tenant)
 * - Shows loading state while checking authentication
 * - Preserves the intended destination URL for redirect after login
 */
const ProtectedRoute = ({ children, requireSuperAdmin = false }) => {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
          <p className="text-slate-600 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Validate the token
  const isTokenValid = validateToken();

  // If not authenticated or token invalid, redirect to appropriate login
  if (!isAuthenticated || !isTokenValid) {
    const redirectPath = getLoginRedirect();
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // If route requires super admin and user is not one, redirect
  if (requireSuperAdmin && !isSuperAdmin) {
    const redirectPath = getLoginRedirect();
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute;
